import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { VOCABULARY } from '@vello/country-vocabularies';
import { type MapConfigView } from '@vello/api-client';
import { Button, Field, FormGrid, Notice, Row, SegmentedControl, Select, Stack, Text, TextInput } from '@vello/web-theme';
import type { TileSource } from '@vello/maps-ui';
import { draftPickup, draftPoint, withAlias, withMapTap, withoutAlias, type PlaceDraft, type PlaceMapTarget } from '../../src/index';
import { tileOverride } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { MapCanvas } from './map-canvas';

/** The codes of a catalog vocabulary, in the catalog's order (the kinds and precisions the form offers). */
export function vocabularyCodes(id: string): readonly string[] {
  const found = VOCABULARY.all.find((v) => v.id === id);
  return found === undefined ? [] : Object.keys(found.labels as Readonly<Record<string, unknown>>);
}

/**
 * The postal-code label of a market's country, from the country pack (`GET /v1/geo/postal-code/format?market=`), in the console's language; undefined with no market, while it loads,
 * when the market has no format, or when the pack has no label for the language - the caller then shows the generic catalog word. Read once per market.
 */
export function usePostalLabel(marketId: string | undefined): string | undefined {
  const { runtime } = useAdmin();
  const { lang } = useT();
  const [found, setFound] = useState<{ readonly market: string; readonly labels: Readonly<Record<string, string>> } | undefined>(undefined);
  useEffect(() => {
    if (marketId === undefined || marketId === '') return undefined;
    let live = true;
    void runtime.geo.postalCodeFormat(marketId).then((res) => {
      if (live) setFound(res.ok ? { market: marketId, labels: res.value.format.labels } : undefined);
    });
    return () => {
      live = false;
    };
  }, [runtime, marketId]);
  if (found === undefined || found.market !== marketId) return undefined;
  const label = found.labels[lang] ?? found.labels[lang.split('-')[0] ?? lang];
  return label !== undefined && label.trim() !== '' ? label.trim() : undefined;
}

/** The raster override of a market's map (none → the platform basemap, D797) and its centre. */
export function usePlaceMapConfig(marketId: string | undefined): { readonly tiles: TileSource | undefined; readonly center: { readonly latitude: number; readonly longitude: number } | undefined } {
  const { runtime, markets } = useAdmin();
  const [config, setConfig] = useState<{ readonly market: string; readonly value: MapConfigView | undefined } | undefined>(undefined);
  useEffect(() => {
    if (marketId === undefined) return undefined;
    let live = true;
    void runtime.ops.mapConfig(marketId).then((res) => {
      if (live) setConfig({ market: marketId, value: res.ok ? res.value : undefined });
    });
    return () => {
      live = false;
    };
  }, [runtime, marketId]);
  const mine = config !== undefined && config.market === marketId ? config.value : undefined;
  const center = mine?.market.center ?? markets.find((m) => m.id === marketId)?.center;
  return { tiles: tileOverride(mine?.tiles), center };
}

/**
 * The map of a place: its point and, when it has one, the pickup point (the entrance). With `onTap` the operator places the point the tap lands on; without it the map only shows.
 * The base map is the platform's own (D797), or the market's raster override when Integrações › Tiles do mapa holds one. Colours are token variables.
 */
export function PlaceMap({
  point,
  pickup,
  tiles,
  center,
  onTap,
}: {
  readonly point: { readonly latitude: number; readonly longitude: number } | undefined;
  readonly pickup: { readonly latitude: number; readonly longitude: number } | null | undefined;
  readonly tiles: TileSource | undefined;
  readonly center: { readonly latitude: number; readonly longitude: number } | undefined;
  readonly onTap?: (p: { readonly latitude: number; readonly longitude: number }) => void;
}): ReactElement {
  const { t } = useT();
  const points = useMemo(
    () => [
      ...(point !== undefined ? [{ id: 'point', kind: 'ride' as const, latitude: point.latitude, longitude: point.longitude, colorVar: 'var(--color-danger)', stale: false }] : []),
      ...(pickup !== null && pickup !== undefined ? [{ id: 'pickup', kind: 'driver' as const, latitude: pickup.latitude, longitude: pickup.longitude, colorVar: 'var(--color-action-primary)', stale: false }] : []),
    ],
    [point, pickup],
  );
  const view = center ?? point;
  return (
    <Stack gap={2}>
      <MapCanvas
        tiles={tiles}
        center={view}
        points={points}
        {...(onTap !== undefined ? { onMapTap: onTap } : {})}
        ariaLabel={t('places.map.label')}
        pointLabel={(p) => (p.id === 'pickup' ? t('places.map.pickup') : t('places.map.point'))}
      />
    </Stack>
  );
}

const TARGETS = ['point', 'pickup'] as const;

/**
 * The create / edit form of a place (D674): the structured fields, the kind and the precision from the catalog, the other names as chips, and a map where the operator
 * taps to place the point and, apart, the pickup point. Every select reads the registries (countries and states by their names, the operating cities of the country);
 * the form never offers a state or a city of another country. `issue` is the first thing wrong, as the catalog's sentence.
 */
export function PlaceForm({
  idPrefix,
  draft,
  onChange,
  disabled,
  editing,
  tiles,
  center,
  issue,
  postalLabel,
}: {
  readonly idPrefix: string;
  readonly draft: PlaceDraft;
  readonly onChange: (draft: PlaceDraft) => void;
  readonly disabled: boolean;
  /** An edit: the country is fixed. */
  readonly editing: boolean;
  readonly tiles: TileSource | undefined;
  readonly center: { readonly latitude: number; readonly longitude: number } | undefined;
  readonly issue: string | undefined;
  /** The country pack's name for the postal code; the generic catalog word when absent. */
  readonly postalLabel?: string | undefined;
}): ReactElement {
  const { t } = useT();
  const { v, reason } = useVocab();
  const place = usePlaceNames();
  const { markets, regions } = useAdmin();
  const [alias, setAlias] = useState('');
  const [target, setTarget] = useState<PlaceMapTarget>('point');

  const countries = useMemo(() => {
    const set = new Set<string>([...markets.map((m) => m.countryCode.toUpperCase()), ...(regions ?? []).map((r) => r.countryCode.toUpperCase())]);
    if (draft.countryCode !== '') set.add(draft.countryCode);
    return [...set].sort((a, b) => place.country(a).localeCompare(place.country(b)));
  }, [markets, regions, draft.countryCode, place]);
  const states = useMemo(() => (regions ?? []).filter((r) => r.countryCode.toUpperCase() === draft.countryCode && (r.status === 'ACTIVE' || r.code === draft.regionCode)), [regions, draft.countryCode, draft.regionCode]);
  const cities = useMemo(() => markets.filter((m) => m.countryCode.toUpperCase() === draft.countryCode && (m.status !== 'RETIRED' || m.id === draft.marketId)), [markets, draft.countryCode, draft.marketId]);
  const kinds = useMemo(() => vocabularyCodes('place.kind'), []);
  const precisions = useMemo(() => vocabularyCodes('place.precision'), []);

  const set = (patch: Partial<PlaceDraft>): void => onChange({ ...draft, ...patch });
  const point = draftPoint(draft.latitude, draft.longitude);
  const pickup = draftPickup(draft);
  const addAlias = (): void => {
    onChange(withAlias(draft, alias));
    setAlias('');
  };
  const pickCountry = (countryCode: string): void => onChange({ ...draft, countryCode, regionCode: '', marketId: '' });
  const pickCity = (marketId: string): void => {
    const city = markets.find((m) => m.id === marketId);
    onChange({ ...draft, marketId, ...(city !== undefined ? { regionCode: city.regionCode ?? draft.regionCode, cityName: draft.cityName === '' ? city.name : draft.cityName } : {}) });
  };

  return (
    <Stack gap={4}>
      <Field label={t('places.field.name')} htmlFor={`${idPrefix}-name`} hint={t('places.form.nameHint')}>
        <TextInput id={`${idPrefix}-name`} value={draft.name} maxLength={200} disabled={disabled} onChange={(e) => set({ name: e.target.value })} />
      </Field>

      <Stack gap={2}>
        <Field label={t('places.field.aliases')} htmlFor={`${idPrefix}-alias`} hint={t('places.form.aliasesHint')}>
          <Row gap={2} align="center">
            <TextInput id={`${idPrefix}-alias`} value={alias} maxLength={200} placeholder={t('places.form.aliasPlaceholder')} disabled={disabled} onChange={(e) => setAlias(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }} />
            <Button variant="secondary" disabled={disabled || alias.trim() === ''} onClick={addAlias}>{t('places.form.aliasAdd')}</Button>
          </Row>
        </Field>
        {draft.aliases.length > 0 && (
          <Row gap={2} wrap>
            {draft.aliases.map((a, i) => (
              <button key={a} type="button" className="vello-chip" aria-label={t('places.form.aliasRemove', { alias: a })} disabled={disabled} onClick={() => onChange(withoutAlias(draft, i))}>
                {a} ×
              </button>
            ))}
          </Row>
        )}
      </Stack>

      <Field label={t('places.field.address')} htmlFor={`${idPrefix}-address`} hint={t('places.form.addressHint')} required>
        <TextInput id={`${idPrefix}-address`} value={draft.formattedAddress} maxLength={400} disabled={disabled} onChange={(e) => set({ formattedAddress: e.target.value })} />
      </Field>

      <FormGrid min="sm">
        <Field label={t('places.field.street')} htmlFor={`${idPrefix}-street`}>
          <TextInput id={`${idPrefix}-street`} value={draft.street} maxLength={200} disabled={disabled} onChange={(e) => set({ street: e.target.value })} />
        </Field>
        <Field label={t('places.field.houseNumber')} htmlFor={`${idPrefix}-number`}>
          <TextInput id={`${idPrefix}-number`} value={draft.houseNumber} maxLength={200} disabled={disabled} onChange={(e) => set({ houseNumber: e.target.value })} />
        </Field>
        <Field label={t('places.field.neighborhood')} htmlFor={`${idPrefix}-neighborhood`}>
          <TextInput id={`${idPrefix}-neighborhood`} value={draft.neighborhood} maxLength={200} disabled={disabled} onChange={(e) => set({ neighborhood: e.target.value })} />
        </Field>
        <Field label={t('places.field.complement')} htmlFor={`${idPrefix}-complement`}>
          <TextInput id={`${idPrefix}-complement`} value={draft.complement} maxLength={200} disabled={disabled} onChange={(e) => set({ complement: e.target.value })} />
        </Field>
        <Field label={postalLabel ?? t('places.field.postalCode')} htmlFor={`${idPrefix}-postal`}>
          <TextInput id={`${idPrefix}-postal`} value={draft.postalCode} maxLength={200} disabled={disabled} onChange={(e) => set({ postalCode: e.target.value })} />
        </Field>
        <Field label={t('places.field.city')} htmlFor={`${idPrefix}-city`}>
          <TextInput id={`${idPrefix}-city`} value={draft.cityName} maxLength={200} disabled={disabled} onChange={(e) => set({ cityName: e.target.value })} />
        </Field>
      </FormGrid>

      <FormGrid min="sm">
        <Field label={t('places.field.country')} htmlFor={`${idPrefix}-country`} required>
          <Select id={`${idPrefix}-country`} value={draft.countryCode} disabled={disabled || editing} onChange={(e) => pickCountry(e.target.value)}>
            <option value="">{t('places.form.chooseCountry')}</option>
            {countries.map((c) => (
              <option key={c} value={c}>{place.country(c)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('places.field.state')} htmlFor={`${idPrefix}-state`}>
          <Select id={`${idPrefix}-state`} value={draft.regionCode} disabled={disabled || draft.countryCode === ''} onChange={(e) => set({ regionCode: e.target.value })}>
            <option value="">{t('places.form.none')}</option>
            {states.map((r) => (
              <option key={r.code} value={r.code}>{r.name}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('places.field.market')} htmlFor={`${idPrefix}-market`}>
          <Select id={`${idPrefix}-market`} value={draft.marketId} disabled={disabled || draft.countryCode === ''} onChange={(e) => pickCity(e.target.value)}>
            <option value="">{t('places.form.noneF')}</option>
            {cities.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('places.field.kind')} htmlFor={`${idPrefix}-kind`}>
          <Select id={`${idPrefix}-kind`} value={draft.kind} disabled={disabled} onChange={(e) => set({ kind: e.target.value })}>
            <option value="">{t('places.form.none')}</option>
            {kinds.map((k) => (
              <option key={k} value={k}>{v('place.kind', k)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('places.field.precision')} htmlFor={`${idPrefix}-precision`}>
          <Select id={`${idPrefix}-precision`} value={draft.precision} disabled={disabled} onChange={(e) => set({ precision: e.target.value })}>
            {precisions.map((p) => (
              <option key={p} value={p}>{v('place.precision', p)}</option>
            ))}
          </Select>
        </Field>
      </FormGrid>

      <Stack gap={2}>
        <SegmentedControl
          ariaLabel={t('places.form.mapTarget')}
          value={target}
          onChange={setTarget}
          options={TARGETS.map((id) => ({ id, label: t(id === 'point' ? 'places.map.point' : 'places.map.pickup') }))}
        />
        <Text as="p" role="caption" tone="secondary">{t('places.form.mapHint')}</Text>
        <PlaceMap point={point} pickup={pickup} tiles={tiles} center={center} {...(disabled ? {} : { onTap: (p: { latitude: number; longitude: number }) => onChange(withMapTap(draft, target, p)) })} />
      </Stack>

      <FormGrid min="sm">
        <Field label={t('places.form.latitude')} htmlFor={`${idPrefix}-lat`} required>
          <TextInput id={`${idPrefix}-lat`} className="tnum" inputMode="decimal" value={draft.latitude} disabled={disabled} onChange={(e) => set({ latitude: e.target.value })} />
        </Field>
        <Field label={t('places.form.longitude')} htmlFor={`${idPrefix}-lng`} required>
          <TextInput id={`${idPrefix}-lng`} className="tnum" inputMode="decimal" value={draft.longitude} disabled={disabled} onChange={(e) => set({ longitude: e.target.value })} />
        </Field>
        <Field label={t('places.form.pickupLatitude')} htmlFor={`${idPrefix}-plat`} hint={t('places.form.pickupHint')}>
          <TextInput id={`${idPrefix}-plat`} className="tnum" inputMode="decimal" value={draft.pickupLatitude} disabled={disabled} onChange={(e) => set({ pickupLatitude: e.target.value })} />
        </Field>
        <Field label={t('places.form.pickupLongitude')} htmlFor={`${idPrefix}-plng`}>
          <TextInput id={`${idPrefix}-plng`} className="tnum" inputMode="decimal" value={draft.pickupLongitude} disabled={disabled} onChange={(e) => set({ pickupLongitude: e.target.value })} />
        </Field>
      </FormGrid>
      {(draft.pickupLatitude !== '' || draft.pickupLongitude !== '') && (
        <Row gap={2}>
          <Button variant="ghost" disabled={disabled} onClick={() => set({ pickupLatitude: '', pickupLongitude: '' })}>{t('places.form.pickupRemove')}</Button>
        </Row>
      )}

      <Text as="p" role="caption" tone="secondary">{t('places.form.verifiedNote')}</Text>
      {issue !== undefined && <Notice tone="danger">{reason(issue)}</Notice>}
    </Stack>
  );
}
